import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, Lock, Clock, CheckCircle2, Phone } from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Checkbox } from '@/Components/ui/checkbox';
import { Label } from '@/Components/ui/label';
import { Text, Heading } from '@/Components/ui/typography';
import { useAuth } from '../../Hooks/useAuth';
import OTPVerificationPopup from '../../Components/OTPVerificationPopup';
import { getApiUrl } from '@/config/api';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

function SignupForm() {
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOTPPopup, setShowOTPPopup] = useState(false);
  const [signupData, setSignupData] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
  
    try {
      // First, send OTP to verify email before creating account
      await sendOTP(email);
      
      // Store form data for later use after OTP verification
      setSignupData({ company, email, password, phone: phone.trim(), whatsapp_consent: whatsappConsent });
      
      // Show OTP popup
      setShowOTPPopup(true);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage('Failed to send verification code. Please try again.');
    }
  };

  const sendOTP = async (email) => {
    try {
      const response = await fetch(getApiUrl('/api/send_otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session management
        body: JSON.stringify({ email: email.trim().toLowerCase(), phone: phone.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage('Failed to send verification code. Please try again.');
    }
  };

  const handleOTPVerificationSuccess = async () => {
    try {
      // Now create the account after OTP verification
      const response = await fetch(getApiUrl('/api/hirewithus-signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // send session cookie so email_verified is visible server-side
        body: JSON.stringify(signupData),
      });

      const data = await response.json();

      if (response.ok) {
        // Account created — now pending admin approval (no auto-login)
        setShowOTPPopup(false);
        setPendingEmail(data.employer_email || email);
        setPendingApproval(true);
      } else {
        setErrorMessage(data.error || 'Account creation failed. Please try again.');
        setShowOTPPopup(false);
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setShowOTPPopup(false);
    }
  };

  const handleOTPPopupClose = () => {
    setShowOTPPopup(false);
    setSignupData(null);
  };

  // ── Pending approval screen ──────────────────────────────────────────────
  if (pendingApproval) {
    return (
      <div className="min-h-screen w-full bg-background flex flex-col justify-center items-center px-6">
        <div className="absolute top-6 left-6 md:top-8 md:left-8">
          <Link to="/hirewithus">
            <img
              src="/Newzenalphablue.png"
              alt="SearchJob.ai Logo"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </Link>
        </div>

        <Card className="w-full max-w-md p-8 md:p-10 text-center shadow-xl border border-border/50">
          <CardContent className="p-0 space-y-5">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
            </div>

            <div className="space-y-2">
              <Heading level="h2" className="text-2xl font-bold text-foreground">
                Account Under Review
              </Heading>
              <Text size="sm" className="text-muted-foreground leading-relaxed">
                Your account has been created successfully. Our team will review your application and send an approval email to:
              </Text>
              <div className="inline-flex items-center gap-2 bg-muted/50 rounded-lg px-4 py-2 mt-1">
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                <span className="text-sm font-medium text-foreground">{pendingEmail}</span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left space-y-1">
              <Text size="sm" className="text-amber-800 font-medium">What happens next?</Text>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>We review your account (usually within 1–2 business days)</li>
                <li>You receive an approval email when ready</li>
                <li>You can then log in and start hiring</li>
              </ul>
            </div>

            <Text size="sm" className="text-muted-foreground">
              Questions? Email us at{' '}
              <a href="mailto:contact@searchjob.ai" className="text-primary hover:underline font-medium">
                contact@searchjob.ai
              </a>
            </Text>

            <Link
              to="/hirewithus/login"
              className="block w-full text-center text-sm text-primary hover:underline font-medium mt-2"
            >
              Back to Login
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col justify-center items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo Section - Top Left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
        <Link 
          to="/hirewithus"
          className="transition-opacity hover:opacity-80"
        >
          <img
            src="/Newzenalphablue.png"
            alt="SearchJob.ai Logo"
            className="h-8 md:h-10 w-auto object-contain drop-shadow-sm"
          />
        </Link>
      </div>
      
      <div className="flex justify-center items-center gap-12 max-w-7xl w-full flex-wrap px-6 py-12 md:flex-nowrap relative z-10">
        <Card className={cn(
          "flex flex-col w-full max-w-[480px] p-8 md:p-10",
          "bg-card border border-border/50",
          "shadow-xl shadow-primary/5",
          "transition-all duration-300",
          "hover:shadow-2xl hover:shadow-primary/10"
        )}>
          <CardContent className="w-full p-0 space-y-6">
            {/* Header */}
            <div className="space-y-3 mb-2">
              <Heading level="h1" className="text-4xl font-bold text-foreground tracking-tight">
                Join us today 👋
              </Heading>
              <Text size="base" variant="muted" className="text-muted-foreground leading-relaxed">
                Together, we create opportunities for growth, collaboration, and innovation – join us to be a part of something bigger.
              </Text>
            </div>

            {/* Form */}
            <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="relative group/company">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/company:text-primary" />
                  <Input
                    type="text"
                    id="company"
                    placeholder="Company name"
                    aria-label="Company Name"
                    className="w-full !pl-10 !pr-3 !h-10 !py-2 transition-all"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    required
                  />
                </div>

                <div className="relative group/email">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/email:text-primary" />
                  <Input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    aria-label="Email Address"
                    className="w-full !pl-10 !pr-3 !h-10 !py-2 transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="relative group/password">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/password:text-primary" />
                  <Input
                    type="password"
                    id="signup-password"
                    placeholder="Enter your password"
                    aria-label="Password"
                    className="w-full !pl-10 !pr-3 !h-10 !py-2 transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="relative group/phone">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within/phone:text-primary" />
                  <Input
                    type="tel"
                    id="phone"
                    placeholder="+91 98765 43210"
                    aria-label="Phone Number"
                    className="w-full !pl-10 !pr-3 !h-10 !py-2 transition-all"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox id="remember" />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal cursor-pointer text-muted-foreground"
                >
                  Remember me
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox id="whatsappConsent" checked={whatsappConsent} onCheckedChange={setWhatsappConsent} />
                <Label htmlFor="whatsappConsent" className="text-sm font-normal cursor-pointer text-muted-foreground">
                  I agree to receive updates from SearchJob.ai on WhatsApp
                </Label>
              </div>

              {errorMessage && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                  <Text size="sm" variant="destructive" className="text-destructive">
                    {errorMessage}
                  </Text>
                </div>
              )}

              <Button 
                type="submit" 
                variant="default"
                size="default"
                className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                  borderColor: getColor('primary')
                }}
              >
                Create Account
              </Button>
            </form>

            {/* Footer */}
            <div className="pt-4 text-center border-t border-border">
              <Text size="sm" variant="muted" className="text-muted-foreground">
                Already have an account?{' '}
                <Link 
                  to="/hirewithus/login" 
                  className={cn(
                    "text-primary hover:text-primary/90 font-bold",
                    "transition-colors underline-offset-4 hover:underline",
                    "text-base"
                  )}
                >
                  Log in
                </Link>
              </Text>
            </div>
          </CardContent>
        </Card>
        
        <div className="hidden lg:block flex-shrink-0">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl blur-2xl" />
            <img 
              className="relative w-full max-w-[520px] aspect-[0.72] object-cover rounded-2xl shadow-2xl"
              loading="lazy"
              src="/login.jpg"
              alt="Sign up illustration"
            />
          </div>
        </div>
      </div>
      
      {/* OTP Verification Popup */}
      <OTPVerificationPopup
        isOpen={showOTPPopup}
        onClose={handleOTPPopupClose}
        email={email}
        onVerificationSuccess={handleOTPVerificationSuccess}
        onResendOTP={() => sendOTP(email)}
      />
    </div>
  );
}

export default SignupForm;
